import React from 'react';
import { Autocomplete, createFilterOptions } from '@mui/material';
import TextField from '@mui/material/TextField';

const filter = createFilterOptions({ matchFrom: 'any', trim: true });

/**
 * @param {Object} props
 * @param {boolean} [props.creatable=false]
 * @param {boolean} [props.disabled=false]
 * @param {boolean} [props.disableClearable=false]
 * @param {string} props.label
 * @param {Function} props.getOptionLabel
 * @param {Function} props.getOptionValue
 * @param {boolean} [props.fullWidth=true]
 * @param {string} props.inputValue
 * @param {Function} props.onChange
 * @param {Function} [props.onCreate=() => null]
 * @param {Array<string|Object>} props.options
 * @param {string|Object} [props.selectedOption=null]
 * @param {Function} [props.setInputValue]
 * @param {string} props.placeholder
 */
export default function Select(props) {
  const {
    creatable = false,
    disabled = false,
    disableClearable = false,
    label,
    getOptionLabel,
    getOptionValue,
    fullWidth = true,
    inputValue,
    onChange,
    onCreate = () => null,
    options,
    selectedOption = null,
    setInputValue,
    placeholder,
  } = props;

  const filterOptions = (opts, params) => {
    const filtered = filter(opts, params);
    const { inputValue: input } = params;

    // Does the input value already exist based on either the value or label function supplied
    const isExisting = opts.some((option) => input === getOptionValue(option)
      || input?.toLowerCase() === getOptionLabel(option)?.toLowerCase());
    if (creatable && input !== undefined && input !== '' && !isExisting) {
      filtered.push({
        name: input,
        key: 'new-creatable-option',
        staged: true,
      });
    }
    return filtered;
  };

  function getMatchingOption(text) {
    const foundOption = options.find((option) => text?.toLowerCase() === getOptionLabel(option)
      ?.toLowerCase());

    return foundOption || null;
  }

  // Handle the distinction between selection of existing value and selection of a creatable option
  const assessChange = (event, value) => {
    // check if creatable and value is a string because `Enter` will submit a string value
    // if creating
    let option = value;
    if (typeof value === 'string') {
      // Find existing by name
      option = getMatchingOption(value)
        || {
          name: value,
          key: 'new-creatable-option',
          staged: true,
        };
    }
    if (creatable && option?.key === 'new-creatable-option') {
      onCreate(option);
    }

    onChange(option);
  };

  // This is to address bad data and hopefully encourage users to contact DEVs when encountered
  const optionLabelFunc = (option) => getOptionLabel(option)
    || option.name
    || 'Error 406: Undefined Value (Please Contact Devs!)';

  function renderOption(optionProps, option) {
    /*
    Note: If this key isn't managed, MUI Autocomplete will utilize its own default key that
    conflicts with ICS data

    Default key is option value and need to override this due to possible list duplicates
    This may be an issue for nasProfiles/vtr or entities like complexes where a duplicate name may
    exist, or when name values are empty/null values;
    */
    const keyOverride = option?.id ? option.id : optionProps.key;
    const optionLabel = optionLabelFunc(option);
    return (
      <li {...optionProps} key={keyOverride}>
        {option.staged ? `Create "${optionLabel}"` : optionLabel}
      </li>
    );
  }

  const getOptionKey = (option, _key) => `${getOptionLabel(option)}`;

  const onInputChange = (event, value) => {
    // Ensure an actual event is fired to avoid duplicate reloading on the initial mount
    const isEnterKey = event && event.type === 'keydown' && event.key && event.key === 'Enter';
    if (setInputValue !== undefined && !isEnterKey) {
      setInputValue(value);
    }
  };

  const isOptionEqualToValue = (option, value) => getOptionValue(option) === getOptionValue(value)
    || inputValue?.toLowerCase() === getOptionLabel(option)?.toLowerCase();

  return (
    <Autocomplete
      disabled={disabled}
      disableClearable={disableClearable}
      value={selectedOption}
      onChange={assessChange}
      filterOptions={filterOptions}
      freeSolo={creatable}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      inputValue={inputValue ?? ''}
      onInputChange={onInputChange}
      options={options}
      getOptionLabel={optionLabelFunc}
      getOptionKey={getOptionKey}
      getOptionDisabled={(opt) => opt.disabled}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={(optionProps, option) => renderOption(optionProps, option)}
      renderInput={(params) => (
        <TextField
          className="input-field"
          {...params}
          slotProps={{
            ...params.slotProps,
            inputLabel: { ...params.slotProps.inputLabel, shrink: true },
          }}
          fullWidth={fullWidth}
          label={label}
          size="small"
          placeholder={placeholder}
        />
      )}
    />
  );
}
